import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../services/adminService';
import { toast } from 'react-toastify';

const AdminListingsTab = () => {
  const [listings, setListings] = useState([]);
  const [flaggedListings, setFlaggedListings] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);

  const loadListings = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'all') {
        const data = await adminService.getAllListings();
        console.log('[AdminListingsTab] All listings API response:', data);
        
        // Extract listings array from response
        if (data && data.listings && Array.isArray(data.listings)) {
          setListings(data.listings);
        } else if (Array.isArray(data)) {
          // If API directly returns an array
          setListings(data);
        } else {
          console.error('[AdminListingsTab] Unexpected data format:', data);
          toast.warning('Received unexpected data format from API');
          setListings([]); // Set empty array as fallback
        }
      } else {
        const data = await adminService.getFlaggedListings();
        console.log('[AdminListingsTab] Flagged listings API response:', data);
        
        // Extract flagged listings array from response
        if (data && data.listings && Array.isArray(data.listings)) {
          setFlaggedListings(data.listings);
        } else if (Array.isArray(data)) {
          // If API directly returns an array
          setFlaggedListings(data);
        } else {
          console.error('[AdminListingsTab] Unexpected data format:', data);
          toast.warning('Received unexpected data format from API');
          setFlaggedListings([]); // Set empty array as fallback
        }
      }
    } catch (error) {
      console.error('[AdminListingsTab] Load listings error:', error);
      toast.error('Failed to load listings');
      // Set empty arrays on error
      if (activeTab === 'all') {
        setListings([]);
      } else {
        setFlaggedListings([]);
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadListings();
  }, [loadListings]);



  const handleUpdateStatus = useCallback(async (id, status) => {
    try {
      await adminService.updateListingStatus(id, { status });
      toast.success('Listing status updated');
      loadListings();
    } catch (error) {
      toast.error('Failed to update listing status');
    }
  }, [loadListings]);

  const renderListing = (listing) => (
    <div key={listing.id} className="bg-white p-4 rounded-lg shadow">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="font-medium">{listing.title}</h3>
          <p className="text-sm text-gray-500">
            Posted by: {listing.donorName} on {listing.createdAt}
          </p>
          <p className="mt-2">{listing.description}</p>
          {listing.flags > 0 && (
            <p className="text-red-600 mt-2">
              ⚠️ Flagged {listing.flags} time(s)
            </p>
          )}
        </div>
        <div className="space-x-2">
          {listing.status === 'active' ? (
            <button
              onClick={() => handleUpdateStatus(listing.id, 'suspended')}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Suspend
            </button>
          ) : (
            <button
              onClick={() => handleUpdateStatus(listing.id, 'active')}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Activate
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex space-x-4 mb-4">
        <button
          className={`px-4 py-2 rounded ${
            activeTab === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700'
          }`}
          onClick={() => setActiveTab('all')}
        >
          All Listings
        </button>
        <button
          className={`px-4 py-2 rounded ${
            activeTab === 'flagged'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700'
          }`}
          onClick={() => setActiveTab('flagged')}
        >
          Flagged Listings
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center p-4">Loading...</div>
      ) : (
        <div className="grid gap-4">
          {activeTab === 'all'
            ? listings.map(renderListing)
            : flaggedListings.map(renderListing)}
        </div>
      )}
    </div>
  );
};

export default AdminListingsTab;
